/* =========================================================
   miyu v5 — core
   Shared by every page: glass + crackle, page colour, header,
   dock, the real Shopify cart (Cart AJAX API), bag, tray, drawer,
   quick view, fly-to-bag, the section registry (theme editor aware)
   and the thread engine.
   ========================================================= */
(function () {
  'use strict';
  if (!window.gsap) return;
  const C = window.MIYU || {};
  const MOTION = window.MOTION, FINE = window.FINE;
  gsap.registerPlugin(ScrollTrigger, SplitText, Flip);
  ScrollTrigger.config({ ignoreMobileResize: true });

  /* ---------- small helpers ---------- */
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => [...(r || document).querySelectorAll(s)];
  const INK = '#22122e', PAPER = '#faf4ec', VIOLET = '#420c69', LAVENDER = '#b8a6e0', LILAC = '#ece4f6', NIGHT = '#2b1745';
  // three page colours only: cream, lilac, night ("linen" is kept as a name for old templates and means lilac)
  const THEMES = { paper: [PAPER, INK], linen: [LILAC, INK], night: [NIGHT, PAPER], coll: [LILAC, INK], worlds: [PAPER, INK], prod: [LILAC, INK] };
  // cents -> "Rs 850" / "Rs 1,200" / "Rs 283.33"
  const rs = c => 'Rs ' + (Math.round(c) / 100).toLocaleString('en-LK', { maximumFractionDigits: 2 });
  // any Shopify CDN image at a given width
  const img = (u, w) => {
    if (!u) return '';
    try { const x = new URL(u, location.href); x.searchParams.set('width', Math.round(w * ((window.devicePixelRatio || 1) > 2 ? 1.5 : 1))); x.searchParams.delete('height'); x.searchParams.delete('crop'); return x.href; } catch (e) { return u; }
  };
  const icon = k => `<svg class="i" viewBox="0 0 24 24"><use href="#i-${k}"/></svg>`;
  const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const emLast = t => { const w = esc(t).trim().split(' '); if (w.length < 2) return w.join(''); const l = w.pop(); return w.join(' ') + ` <em>${l}</em>`; };
  const loadImg = url => new Promise((res, rej) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = rej; i.src = url; });
  const pagePos = el => { let x = 0, y = 0, e = el; while (e) { x += e.offsetLeft; y += e.offsetTop; e = e.offsetParent; } return { x, y, w: el.offsetWidth, h: el.offsetHeight }; };

  // the scroll position, read in the scroll event (layout is clean there). Per-frame code reads Miyu.sy instead of
  // window.scrollY: reading scrollY after another script has moved something in the same frame forces a full layout.
  let sy = scrollY; addEventListener('scroll', () => { sy = scrollY; }, { passive: true });
  const Miyu = window.Miyu = { get sy() { return sy; }, $, $$, rs, img, icon, esc, emLast, loadImg, pagePos, INK, PAPER, VIOLET, LAVENDER, LILAC, NIGHT, THEMES, MOTION, FINE, config: C };
  document.querySelectorAll('svg.i').forEach(s => s.getAttribute('viewBox') || s.setAttribute('viewBox', '0 0 24 24'));

  /* =========================================================
     CRACKLE TEXTURE — real cellular fracture lines, generated once
     ========================================================= */
  (function crackle() {
    const S = 256, cv = document.createElement('canvas'); cv.width = cv.height = S;
    const c = cv.getContext('2d'), im = c.createImageData(S, S);
    let seed = 7; const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
    const layer = (cells, width, weight) => {
      const cs = S / cells, pts = [];
      for (let y = 0; y < cells; y++) for (let x = 0; x < cells; x++) pts.push([(x + 0.15 + rnd() * 0.7) * cs, (y + 0.15 + rnd() * 0.7) * cs]);
      return (px, py) => {
        const gx = Math.floor(px / cs), gy = Math.floor(py / cs); let f1 = 1e9, f2 = 1e9;
        for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
          const cx = (gx + i + cells) % cells, cy = (gy + j + cells) % cells, p = pts[cy * cells + cx];
          const ox = (gx + i < 0 ? -S : gx + i >= cells ? S : 0), oy = (gy + j < 0 ? -S : gy + j >= cells ? S : 0);
          const d = Math.hypot(px - (p[0] + ox), py - (p[1] + oy));
          if (d < f1) { f2 = f1; f1 = d; } else if (d < f2) f2 = d;
        }
        return Math.pow(Math.max(0, 1 - (f2 - f1) / width), 1.6) * weight;
      };
    };
    const a = layer(5, 1.8, 1), b = layer(11, 1.2, 0.55);
    for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
      const v = Math.min(1, Math.max(a(x, y), b(x, y))), k = (y * S + x) * 4;
      im.data[k] = im.data[k + 1] = im.data[k + 2] = 255; im.data[k + 3] = v * 255;
    }
    c.putImageData(im, 0, 0);
    Miyu.crackleURL = cv.toDataURL();
    document.documentElement.style.setProperty('--crackle', `url(${Miyu.crackleURL})`);
  })();

  // the crackle catches the light only when something happens (cheap: no constant repaint)
  const glint = Miyu.glint = el => { if (!el || !MOTION) return; el.classList.remove('glint'); void el.offsetWidth; el.classList.add('glint'); clearTimeout(el._g); el._g = setTimeout(() => el.classList.remove('glint'), 1400); };
  let scrollStopT = 0;
  addEventListener('scroll', () => { clearTimeout(scrollStopT); scrollStopT = setTimeout(() => glint(document.getElementById('dock')), 220); }, { passive: true });

  /* =========================================================
     LIQUID GLASS — edge-refraction maps for the dock, bag and bars
     (Chromium bends what's behind; other browsers keep the frosted fallback)
     ========================================================= */
  function lensFilter(id, w, h, r, bezel, strength) {
    const old = document.getElementById(id); if (old) old.remove();
    const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
    const c = cv.getContext('2d'), im = c.createImageData(w, h);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const px = x + 0.5 - w / 2, py = y + 0.5 - h / 2;
      const qx = Math.abs(px) - (w / 2 - r), qy = Math.abs(py) - (h / 2 - r);
      let nx, ny, d;
      if (qx > 0 && qy > 0) { const l = Math.hypot(qx, qy) || 1; d = r - l; nx = qx / l * Math.sign(px); ny = qy / l * Math.sign(py); }
      else if (qx > qy) { d = r - qx; nx = Math.sign(px); ny = 0; } else { d = r - qy; nx = 0; ny = Math.sign(py); }
      const t = Math.max(0, Math.min(1, 1 - d / bezel)), m = t * t * (3 - 2 * t);
      const k = (y * w + x) * 4;
      im.data[k] = 128 - nx * m * 127; im.data[k + 1] = 128 - ny * m * 127; im.data[k + 2] = 128; im.data[k + 3] = 255;
    }
    c.putImageData(im, 0, 0);
    const NS = 'http://www.w3.org/2000/svg', f = document.createElementNS(NS, 'filter');
    f.setAttribute('id', id); f.setAttribute('x', 0); f.setAttribute('y', 0); f.setAttribute('width', w); f.setAttribute('height', h);
    f.setAttribute('filterUnits', 'userSpaceOnUse'); f.setAttribute('primitiveUnits', 'userSpaceOnUse'); f.setAttribute('color-interpolation-filters', 'sRGB');
    f.innerHTML = `<feImage href="${cv.toDataURL()}" x="0" y="0" width="${w}" height="${h}" preserveAspectRatio="none" result="map"/><feGaussianBlur in="SourceGraphic" stdDeviation="1.2" result="soft"/><feDisplacementMap in="soft" in2="map" scale="${strength}" xChannelSelector="R" yChannelSelector="G"/>`;
    const defs = document.getElementById('glassDefs'); defs && defs.appendChild(f);
  }
  const isChromium = !!window.chrome && CSS.supports('backdrop-filter', 'url(#x)');
  const liquid = Miyu.liquid = (el, id, bezel, strength) => {
    if (!isChromium || !el) return;
    const r = el.getBoundingClientRect(), w = Math.round(r.width), h = Math.round(r.height);
    if (!w || !h) return;
    lensFilter(id, w, h, Math.min(h / 2, parseFloat(getComputedStyle(el).borderRadius) || h / 2), bezel, strength);
    el.style.backdropFilter = el.style.webkitBackdropFilter = `url(#${id}) saturate(1.9) brightness(1.04)`;
  };

  /* smooth scroll on desktop only — phones keep native momentum */
  let lenis = null;
  if (MOTION && FINE && window.Lenis && !C.designMode) {
    lenis = new Lenis({ lerp: 0.085 });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(t => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
  }
  Miyu.lenis = lenis;
  const scrollToY = Miyu.scrollToY = y => lenis ? lenis.scrollTo(y, { duration: 1.2 }) : scrollTo({ top: y, behavior: MOTION ? 'smooth' : 'auto' });
  const lock = Miyu.lock = on => { document.documentElement.style.overflow = on ? 'hidden' : ''; lenis && (on ? lenis.stop() : lenis.start()); };

  /* =========================================================
     PAGE COLOUR — one resolver, whatever sits at 55% of the screen.
     A section can offer its own colour: el._themeHook = () => [bg, fg]
     ========================================================= */
  let themeSecs = [], themeKey = '', varBg = '', varFg = '', varT = null;
  // Only the painted background animates (body + the few sticky bands that must match it): that is a cheap repaint.
  // The --bg/--fg variables that everything else reads change ONCE per section, never per frame — animating a
  // root variable restyles the whole page every frame, which is what made phones stutter.
  // the page colour lives on one fixed, screen-sized layer behind everything: repainting it is nearly free,
  // where repainting the body means re-recording the whole page
  const pageBg = document.createElement('div'); pageBg.id = 'pageBg'; pageBg.setAttribute('aria-hidden', 'true'); document.body.prepend(pageBg);
  // sticky bands that must match the page colour follow it too, but only while they're on screen
  const bgFollowers = () => [pageBg, ...$$('.wv-band, [data-bg-follow]').filter(b => { const r = b.getBoundingClientRect(); return r.bottom > 0 && r.top < innerHeight; })];
  const syncBands = () => $$('.wv-band, [data-bg-follow]').forEach(b => { b.style.backgroundColor = pageBg.style.backgroundColor; });
  function setTheme(bg, fg, baseBg) {
    const d = MOTION ? 0.8 : 0, root = document.documentElement.style;
    gsap.to(bgFollowers(), { backgroundColor: bg, duration: d, ease: 'power2.out', overwrite: 'auto', onComplete: syncBands });
    // cancel any colour change still waiting first: a quick scroll through lilac and back to cream must not let
    // the older, late "lilac" land after the page has already gone cream
    varT && varT.kill(); varT = null;
    if (baseBg === varBg && fg === varFg) return;
    const apply = () => { if (baseBg !== varBg) { varBg = baseBg; root.setProperty('--bg', baseBg); } if (fg !== varFg) { varFg = fg; root.setProperty('--fg', fg); const dark = fg === PAPER; root.setProperty('--ac', dark ? LAVENDER : VIOLET); root.setProperty('--on-ac', dark ? NIGHT : PAPER); } };
    if (!d || !varFg) apply(); else varT = gsap.delayedCall(d * 0.4, apply);   // text flips as the new colour takes over
  }
  const resolveTheme = Miyu.resolveTheme = () => {
    const y = innerHeight * 0.55;
    let sec = null;
    for (const s of themeSecs) { const r = s.getBoundingClientRect(); if (r.top <= y && r.bottom > y) sec = s; }
    const key = sec ? sec.dataset.theme : 'paper', base = THEMES[key] || THEMES.paper;
    const [bg, fg] = (sec && sec._themeHook && sec._themeHook()) || base;
    if (bg + fg === themeKey) return;
    themeKey = bg + fg; setTheme(bg, fg, base[0]);
  };
  const collectThemes = () => { themeSecs = $$('[data-theme]'); };
  addEventListener('scroll', () => requestAnimationFrame(resolveTheme), { passive: true });

  /* =========================================================
     THE REAL CART — Cart AJAX API. Everything that shows your
     picks (bag, beads, tray, sockets, rope, rail) listens here.
     Totals always come from Shopify's /cart.js.
     ========================================================= */
  const products = new Map();   // variant id -> product info, from the JSON blobs sections render
  Miyu.products = products;
  Miyu.addProducts = list => (list || []).forEach(p => { if (p && p.vid) { products.set(p.vid, p); products.set('h:' + p.h, p); } });
  const readBlobs = root => $$('script[data-products]', root).forEach(s => { try { Miyu.addProducts(JSON.parse(s.textContent)); } catch (e) { console.warn('products json', e); } });

  const cart = Miyu.cart = (function () {
    const R = C.routes || {};
    const url = (r, fb) => (r || fb) + '.js';
    let data = null, busy = Promise.resolve(), gen = 0, pending = 0;
    const subs = new Set();
    try { const j = document.getElementById('cartJson'); if (j) data = JSON.parse(j.textContent); } catch (e) { data = null; }
    if (!data) data = { items: [], item_count: 0, total_price: 0, original_total_price: 0, total_discount: 0 };
    const emit = meta => subs.forEach(fn => { try { fn(data, meta || {}); } catch (e) { console.error(e); } });
    const recount = () => { data.item_count = data.items.reduce((a, i) => a + i.quantity, 0); };
    async function req(u, body) {
      const r = await fetch(u, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw Object.assign(new Error(j.description || j.message || 'We couldn’t update your bag. Please try again.'), { status: r.status });
      return j;
    }
    // Shopify's answer is only shown once every tap you've made has reached it: an answer to an earlier tap
    // doesn't know about the later ones, and painting it would bring back a piece you just took out
    const apply = (j, meta) => { ++gen; data = j; emit(Object.assign({ real: true }, meta)); return data; };
    async function refresh(meta) {
      const my = ++gen;
      try {
        const r = await fetch(url(R.cart, '/cart'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const j = await r.json();
        if (my === gen && !pending) { data = j; emit(Object.assign({ real: true }, meta)); }
      } catch (e) { console.warn('cart refresh', e); }
      return data;
    }
    // one change at a time, in the order you made them
    function queue(job, meta) {
      pending++;
      return (busy = busy.then(async () => {
        let j = null;
        try { j = await job(); } catch (e) { Miyu.toast(e.message); }
        pending--;
        if (pending) return data;
        return j && Array.isArray(j.items) && 'item_count' in j ? apply(j, meta) : refresh(meta);
      }));
    }
    // quantities are set by variant id (it never changes); line keys can change under us when discounts apply,
    // and a stale key is what Shopify answers with "no valid id or line parameter". /cart/update is also fine
    // with setting 0 on something that's already gone.
    const setVariant = (vid, q) => req(url(R.cartUpdate, '/cart/update'), { updates: { [vid]: q } });
    const line = vid => data.items.find(i => i.variant_id === +vid);
    const api = {
      get data() { return data; },
      get count() { return data.item_count || 0; },
      get vids() { const s = []; data.items.forEach(i => { if (!s.includes(i.variant_id)) s.push(i.variant_id); }); return s; },
      get busy() { return pending > 0; },
      has: vid => !!line(vid),
      line,
      on(fn) { subs.add(fn); return () => subs.delete(fn); },
      refresh,
      add(vid, opts) {
        opts = opts || {}; vid = +vid;
        const qty = opts.qty || 1, p = products.get(vid);
        // optimistic: the bead lands now, Shopify confirms a moment later
        if (!line(vid)) {
          data.items.push({ key: 'opt-' + vid, variant_id: vid, quantity: qty, product_title: p ? p.n : '', title: p ? p.n : '', image: p ? p.img : '', url: p ? p.u : '', handle: p ? p.h : '', price: p ? p.p : 0, final_price: p ? p.p : 0, original_line_price: p ? p.p * qty : 0, final_line_price: p ? p.p * qty : 0, _opt: true });
        } else line(vid).quantity += qty;
        recount(); emit({ from: opts.from, added: vid });
        return queue(() => req(url(R.cartAdd, '/cart/add'), { items: [{ id: vid, quantity: qty }] }), { added: vid });
      },
      remove(vid, opts) {
        vid = +vid; if (!line(vid)) return Promise.resolve(data);
        data.items = data.items.filter(i => i.variant_id !== vid); recount(); emit({ removed: vid, from: opts && opts.from });
        return queue(() => setVariant(vid, 0), { removed: vid });
      },
      setQty(key, q) {
        const l = data.items.find(i => i.key === key); if (!l) return Promise.resolve(data);
        if (q <= 0) return api.remove(l.variant_id);
        l.quantity = q; recount(); emit({});
        return queue(() => setVariant(l.variant_id, q), {});
      },
      toggle(vid, opts) { return line(vid) ? api.remove(vid, opts) : api.add(vid, opts); }
    };
    // back/forward cache and other tabs: fetch the truth again
    addEventListener('pageshow', e => { if (e.persisted) refresh(); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refresh(); });
    return api;
  })();

  /* =========================================================
     SAVED — the pieces you keep (butterflies), on this device.
     Product handles, newest first. Other tabs stay in step.
     ========================================================= */
  Miyu.saved = (function () {
    const KEY = 'miyu-saved', subs = new Set();
    const get = () => { try { const v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v.filter(h => typeof h === 'string') : []; } catch (e) { return []; } };
    const emit = meta => { const l = get(); subs.forEach(fn => { try { fn(l, meta || {}); } catch (e) { console.error(e); } }); };
    const set = (l, meta) => { try { localStorage.setItem(KEY, JSON.stringify(l)); } catch (e) {} emit(meta); };
    addEventListener('storage', e => { if (e.key === KEY) emit(); });
    // pieces saved on the old site (its wishlist kept product handles under 'miyu-wishlist') come across once
    try {
      if (!localStorage.getItem('miyu-wishlist-moved')) {
        const old = JSON.parse(localStorage.getItem('miyu-wishlist') || '[]');
        if (Array.isArray(old) && old.length) { const cur = get(); localStorage.setItem(KEY, JSON.stringify(cur.concat(old.filter(h => typeof h === 'string' && !cur.includes(h))))); }
        localStorage.setItem('miyu-wishlist-moved', '1');
      }
    } catch (e) {}
    const api = {
      get list() { return get(); },
      has: h => get().includes(h),
      toggle(h) { const l = get(), on = !l.includes(h); set(on ? [h].concat(l) : l.filter(x => x !== h), { h, on }); return on; },
      remove(h) { set(get().filter(x => x !== h), { h, on: false }); },
      on(fn) { subs.add(fn); return () => subs.delete(fn); }
    };
    // the count on the dock's Saved butterfly
    const badge = l => $$('[data-saved-n]').forEach(b => { b.textContent = l.length || ''; b.classList.toggle('on', l.length > 0); });
    api.on(badge); badge(get());
    return api;
  })();

  // what a cart line looks like to the UI (registry first, the cart line itself as fallback)
  Miyu.info = vid => {
    const p = products.get(+vid), l = cart.line(vid);
    return {
      vid: +vid, n: (p && p.n) || (l && l.product_title) || '', img: (p && p.img) || (l && (l.image || (l.featured_image && l.featured_image.url))) || '',
      p: (l && l.final_price) || (p && p.p) || 0, t: (p && p.t) || '#ebe4f5', u: (p && p.u) || (l && l.url) || '#', f: (p && p.f) || '', bf: (p && p.bf) || ''
    };
  };

  /* offer copy, from Theme settings (the discount itself lives in Shopify) */
  const O = C.offer || { qty: 2, pct: 20, freeQty: 3, fee: 40000, freeAmt: 0 };
  Miyu.offer = O;
  Miyu.trayCopy = n => n <= 0 ? '' : n < O.qty ? `Add ${O.qty - n} more — ${O.pct}% off` : n < O.freeQty ? `${O.freeQty - n} more — free delivery` : `${O.pct}% off + free delivery`;

  /* a small glass note for errors ("only 1 left") */
  Miyu.toast = msg => {
    let t = document.getElementById('toast');
    if (!t) { t = document.createElement('div'); t.id = 'toast'; t.className = 'toast lg'; t.setAttribute('role', 'status'); document.body.appendChild(t); }
    t.innerHTML = `<span>${esc(msg)}</span>`; t.classList.add('on'); glint(t);
    clearTimeout(t._t); t._t = setTimeout(() => t.classList.remove('on'), 4200);
  };

  /* =========================================================
     FLY TO BAG — the photo becomes a bead and drops into the bag
     ========================================================= */
  Miyu.flyToBag = (fromEl, imgUrl, done) => {
    const bag = document.getElementById('bagBtn');
    if (!MOTION || !fromEl || !bag) return done();
    const r = fromEl.getBoundingClientRect(), b = bag.getBoundingClientRect(), size = Math.max(60, Math.min(130, r.width * 0.5));
    const el = document.createElement('div'); el.className = 'flyb';
    Object.assign(el.style, { width: size + 'px', height: size + 'px', backgroundImage: `url(${img(imgUrl, 300)})` });
    document.body.appendChild(el);
    const x0 = r.left + r.width / 2 - size / 2, y0 = r.top + r.height / 2 - size / 2, x1 = b.left + b.width / 2 - size / 2, y1 = b.top + b.height / 2 - size / 2;
    gsap.timeline({ onComplete: () => el.remove() })
      .fromTo(el, { x: x0, y: y0, scale: 0.4, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(2)' })
      .to(el, { x: x1, duration: 0.85, ease: 'power2.inOut' })
      .to(el, { y: y1, duration: 0.85, ease: 'back.in(1.5)' }, '<')
      .to(el, { scale: 20 / size, duration: 0.85, ease: 'power2.in' }, '<')
      .call(done)
      .to(el, { opacity: 0, duration: 0.15 });
  };

  /* =========================================================
     SECTION REGISTRY — each section declares what it does;
     the theme editor can add, remove and reorder them live.
     ========================================================= */
  const defs = {}, live = new Map();
  Miyu.define = (type, fn) => { defs[type] = fn; };
  Miyu.instance = el => live.get(el);
  function makeLife(el) {
    const L = { el, offs: [], ticks: [], obs: [], timers: [], ctx: gsap.context(() => {}, el) };
    L.on = (t, ev, fn, o) => { t.addEventListener(ev, fn, o); L.offs.push(() => t.removeEventListener(ev, fn, o)); return fn; };
    L.tick = fn => { gsap.ticker.add(fn); L.ticks.push(fn); return fn; };
    L.io = (target, cb, o) => { const io = new IntersectionObserver(cb, o); io.observe(target); L.obs.push(io); return io; };
    L.ro = (target, cb) => { const ro = new ResizeObserver(cb); ro.observe(target); L.obs.push(ro); return ro; };
    L.timeout = (fn, ms) => { const t = setTimeout(fn, ms); L.timers.push(t); return t; };
    L.gsap = fn => L.ctx.add(fn);
    L.cart = fn => { const off = cart.on(fn); L.offs.push(off); return fn; };
    L.saved = fn => { const off = Miyu.saved.on(fn); L.offs.push(off); return fn; };
    return L;
  }
  function splitHeads(el, L) {
    if (!MOTION) return;
    L.gsap(() => $$('[data-split]', el).forEach(h => {
      SplitText.create(h, { type: 'lines', mask: 'lines', autoSplit: true, onSplit: self =>
        gsap.from(self.lines, { yPercent: 115, duration: 1.3, ease: 'expo.out', stagger: 0.1, scrollTrigger: { trigger: h, start: 'top 84%' } }) });
    }));
  }
  // line drawings ([data-draw] holding .dl paths) draw themselves the first time they're seen —
  // inside a closed accordion that's when it opens
  function drawIn(el, L) {
    const ds = $$('[data-draw]', el); if (!ds.length) return;
    if (!MOTION) { ds.forEach(d => $$('.dl', d).forEach(p => { p.style.strokeDashoffset = 0; })); return; }
    ds.forEach(d => L.io(d, (es, io) => {
      if (!es.some(e => e.isIntersecting)) return; io.disconnect();
      gsap.fromTo($$('.dl:not(.dl-dash)', d), { strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 1.1, stagger: 0.1, ease: 'power2.inOut' });
      const soft = $$('.dl-dash, text', d); soft.length && gsap.fromTo(soft, { opacity: 0 }, { opacity: 1, duration: 0.6, delay: 0.8 });
    }, { rootMargin: '0px 0px -12% 0px' }));
  }
  function magnetic(el, L) {
    if (!(FINE && MOTION)) return;
    $$('[data-magnetic]', el).forEach(b => {
      const mx = gsap.quickTo(b, 'x', { duration: 0.6, ease: 'elastic.out(1, .4)' }), my = gsap.quickTo(b, 'y', { duration: 0.6, ease: 'elastic.out(1, .4)' });
      L.on(b, 'pointermove', e => { const r = b.getBoundingClientRect(); mx((e.clientX - r.left - r.width / 2) * 0.28); my((e.clientY - r.top - r.height / 2) * 0.35); });
      L.on(b, 'pointerleave', () => { mx(0); my(0); });
    });
  }
  function mount(el) {
    if (live.has(el)) return;
    const L = makeLife(el), fn = defs[el.dataset.section];
    const inst = { L, api: null };
    live.set(el, inst);
    readBlobs(el);
    if (fn) { try { L.ctx.add(() => { inst.api = fn(el, L) || null; }); } catch (e) { console.error('section', el.dataset.section, e); } }
    splitHeads(el, L); magnetic(el, L); drawIn(el, L);
  }
  function unmount(el) {
    const inst = live.get(el); if (!inst) return;
    const L = inst.L;
    try { inst.api && inst.api.destroy && inst.api.destroy(); } catch (e) { console.error(e); }
    L.offs.forEach(f => f()); L.ticks.forEach(f => gsap.ticker.remove(f)); L.obs.forEach(o => o.disconnect()); L.timers.forEach(clearTimeout);
    L.ctx.revert();
    live.delete(el);
  }
  let refreshT = 0;
  const refresh = Miyu.refresh = () => { clearTimeout(refreshT); refreshT = setTimeout(() => { collectThemes(); dockTriggers(); ScrollTrigger.refresh(); resolveTheme(); }, 60); };
  document.addEventListener('shopify:section:load', e => { $$('[data-section]', e.target).forEach(mount); refresh(); cart.refresh(); });
  document.addEventListener('shopify:section:unload', e => { $$('[data-section]', e.target).forEach(unmount); refresh(); });
  document.addEventListener('shopify:section:reorder', refresh);
  ['shopify:block:select', 'shopify:block:deselect', 'shopify:section:select'].forEach(ev => document.addEventListener(ev, e => {
    const host = e.target.closest('[data-section]') || $('[data-section]', e.target); const inst = host && live.get(host);
    inst && inst.api && inst.api.onEditor && inst.api.onEditor(ev, e);
  }));

  /* =========================================================
     HEADER + DOCK + TRAY + BAG
     ========================================================= */
  function chrome() {
    // header: the wordmark steps aside while you read; the bag bead stays
    if ($('#logo')) ScrollTrigger.create({ start: 140, end: 'max', onUpdate: s => gsap.to('#logo', { y: s.direction === 1 ? -70 : 0, opacity: s.direction === 1 ? 0 : 1, duration: 0.5, ease: 'power3.out', overwrite: 'auto' }), onLeaveBack: () => gsap.to('#logo', { y: 0, opacity: 1, duration: 0.4 }) });
    const dock = $('#dock');
    if (dock) {
      gsap.set(dock, { xPercent: -50 });
      liquid(dock, 'lgDock', 22, 34);
      const lensEl = $('#lens', dock), items = $$('.dk', dock), base = +dock.dataset.active || 0;
      let cur = -1;
      const moveTo = Miyu.dockTo = (i, instant) => {
        if (i < 0 || !items[i]) return; cur = i;
        items.forEach((it, k) => { it.classList.toggle('on', k === i); k === i ? it.setAttribute('aria-current', 'page') : it.removeAttribute('aria-current'); });
        const r = items[i].getBoundingClientRect(), rr = dock.getBoundingClientRect();
        gsap.to(lensEl, { x: r.left - rr.left + 4, width: r.width - 8, duration: instant || !MOTION ? 0 : 0.7, ease: 'elastic.out(1, .65)' });
        if (!instant) glint(dock);
        if (!instant && MOTION) gsap.fromTo(lensEl, { scaleY: 0.8, scaleX: 1.15 }, { scaleY: 1, scaleX: 1, duration: 0.6, ease: 'elastic.out(1, .5)' });
      };
      Miyu.dockBase = base;
      items.forEach((it, i) => it.addEventListener('click', e => {
        moveTo(i);
        if (it.dataset.go === 'top' && C.template === 'index') { e.preventDefault(); scrollToY(0); }
      }));
      moveTo(base, true);
      addEventListener('resize', () => moveTo(cur < 0 ? base : cur, true));
    }
    const bag = $('#bagBtn');
    liquid(bag, 'lgBag', 16, 22);
    bag && bag.addEventListener('click', () => Miyu.drawer.open());
    const tray = $('#tray');
    if (tray) {
      gsap.set(tray, { xPercent: -50, y: 70, scale: 0.6, opacity: 0 });
      $('#trayView', tray).addEventListener('click', e => { e.preventDefault(); Miyu.drawer.open(); });
    }
  }
  // "Shop" lights up while you're in the shopping sections of a page
  let dockST = [];
  function dockTriggers() {
    dockST.forEach(s => s.kill()); dockST = [];
    const secs = $$('[data-dock]'); if (!secs.length || !Miyu.dockTo) return;
    const on = new Set();
    secs.forEach(s => dockST.push(ScrollTrigger.create({ trigger: s, start: 'top 60%', end: 'bottom 40%', onToggle: t => { t.isActive ? on.add(s) : on.delete(s); Miyu.dockTo(on.size ? +s.dataset.dock : Miyu.dockBase); } })));
  }

  // hide the tray while a section that already shows the stack is on screen
  const trayHolds = new Set();
  Miyu.trayHold = (key, on) => { on ? trayHolds.add(key) : trayHolds.delete(key); renderBag({}); };
  let lastCount = -1;
  function renderBag(meta) {
    const n = cart.count, vids = cart.vids;
    const bc = $('#bagc'), bb = $('#bagBeads'), bag = $('#bagBtn');
    if (bc) {
      if (n !== lastCount && lastCount > -1) { glint(bag); MOTION && gsap.fromTo(bag, { scale: 0.86 }, { scale: 1, duration: 0.8, ease: 'elastic.out(1, .4)' }); }
      bc.textContent = n; bc.classList.toggle('on', n > 0);
      bag.setAttribute('aria-label', `Bag, ${n} item${n === 1 ? '' : 's'}`);
      bb.innerHTML = vids.slice(0, 3).map(v => `<i style="background-image:url(${img(Miyu.info(v).img, 40)});${esc(Miyu.info(v).bf)}"></i>`).join('');
    }
    lastCount = n;
    const tray = $('#tray');
    if (tray) {
      $('#mini', tray).innerHTML = vids.slice(0, 3).map(v => `<i style="background-image:url(${img(Miyu.info(v).img, 80)});${esc(Miyu.info(v).bf)}"></i>`).join('');
      $('#trayText', tray).textContent = Miyu.trayCopy(n);
      const show = n > 0 && !trayHolds.size && !Miyu.drawer.isOpen;
      if (show && !tray._on) glint(tray);
      tray._on = show;
      gsap.to(tray, { y: show ? 0 : 70, scale: show ? 1 : 0.6, opacity: show ? 1 : 0, pointerEvents: show ? 'auto' : 'none', duration: MOTION ? 0.8 : 0, ease: show ? 'elastic.out(1, .6)' : 'power2.in', overwrite: 'auto' });
    }
    const live = $('#liveMsg'); if (live && meta && (meta.added || meta.removed) && meta.real) live.textContent = `Bag updated: ${n} item${n === 1 ? '' : 's'}`;
  }

  /* =========================================================
     THE DRAWER — your bag, with Shopify's own numbers
     ========================================================= */
  Miyu.drawer = (function () {
    const d = $('#drawer'); if (!d) return { open() { location.href = (C.routes && C.routes.cart) || '/cart'; }, isOpen: false };
    const panel = $('.dr-panel', d), slots = $('#drSlots', d), rack = $('#drRack', d), sum = $('#drSum', d), msg = $('#drMsg', d);
    const thread = $('#drThread', d), tsvg = $('.dr-thread', d), fly = $('#drFly', d);
    let isOpen = false, lastFocus = null, wasDone = null;
    function discounts(c) {
      const by = {};
      c.items.forEach(i => (i.line_level_discount_allocations || []).forEach(a => { const t = (a.discount_application && a.discount_application.title) || 'Offer'; by[t] = (by[t] || 0) + a.amount; }));
      (c.cart_level_discount_applications || []).forEach(a => { const t = a.title || 'Offer'; by[t] = (by[t] || 0) + (a.total_allocated_amount || 0); });
      return Object.entries(by).filter(([, v]) => v > 0);
    }
    // what the n-th piece unlocks
    const unlock = p => p === O.qty ? `${O.pct}% off` : p === O.freeQty ? 'free delivery' : '';
    // the thread: from the left edge, through every ring, sagging between them, off the right edge
    function drawThread() {
      const rings = $$('.dr-ring', slots); if (!rings.length) return;
      const w = Math.max(slots.scrollWidth, rack.clientWidth), rr = rack.getBoundingClientRect(), sl = rack.scrollLeft;
      const pts = rings.map(r => { const b = r.getBoundingClientRect(); return [b.left + b.width / 2 - rr.left + sl, b.top + b.height / 2 - rr.top]; });
      const y0 = pts[0][1], sag = 16;
      let p = `M0 ${y0 - 6}`, prev = [0, y0 - 6];
      [...pts, [w, y0 - 6]].forEach(q => { const mx = (prev[0] + q[0]) / 2, my = Math.max(prev[1], q[1]) + sag * Math.min(1, (q[0] - prev[0]) / 120); p += ` Q${mx.toFixed(1)} ${my.toFixed(1)} ${q[0].toFixed(1)} ${q[1].toFixed(1)}`; prev = q; });
      tsvg.setAttribute('width', w); tsvg.setAttribute('viewBox', `0 0 ${w} ${rack.clientHeight}`); tsvg.style.width = w + 'px';
      thread.setAttribute('d', p);
      // the butterfly's perch: the low point of the thread between the 2nd and 3rd pieces (or the last gap)
      const k = Math.min(pts.length - 1, Math.max(1, O.freeQty - 1)), a = pts[k - 1], b = pts[k];
      if (a && b) { fly.style.left = ((a[0] + b[0]) / 2) + 'px'; fly.style.top = (Math.max(a[1], b[1]) + sag * 0.5) + 'px'; }
    }
    function land() {
      if (!MOTION) { gsap.set(fly, { opacity: 1 }); return; }
      const wings = $('svg', fly);
      gsap.killTweensOf([fly, wings]);
      gsap.fromTo(fly, { x: 140, y: -110, rotation: 18, opacity: 0 }, { x: 0, y: 0, rotation: 0, opacity: 1, duration: 1.6, ease: 'power2.out' });
      // wing beats while it flies in, then one slow fold as it settles
      gsap.fromTo(wings, { scaleX: 1 }, { scaleX: 0.25, duration: 0.14, repeat: 9, yoyo: true, ease: 'sine.inOut',
        onComplete: () => gsap.to(wings, { keyframes: [{ scaleX: 0.55, duration: 0.35 }, { scaleX: 1, duration: 0.5 }], ease: 'sine.inOut' }) });
    }
    function render(c) {
      const n = c.item_count || 0;
      const freeByAmt = O.freeAmt > 0 && c.total_price >= O.freeAmt, free = n >= O.freeQty || freeByAmt, done = n >= O.freeQty;
      d.classList.toggle('is-empty', n === 0);
      d.classList.toggle('done', done);
      $('#drCount', d).textContent = n ? `${n} piece${n === 1 ? '' : 's'}` : '';
      msg.innerHTML = done ? `Your three are complete: <b>${O.pct}% off + free delivery.</b>`
        : n === 0 ? `Nothing strung yet. Wear it in threes: <b>${O.qty} take ${O.pct}% off</b>, ${O.freeQty} bring free delivery.`
        : n < O.qty ? `<b>Add ${O.qty - n} more</b> for ${O.pct}% off everything.`
        : `<b>${O.pct}% off is on.</b> ${free ? 'Delivery is already free.' : `${O.freeQty - n} more and delivery is free.`}`;
      // your pieces, hanging from the thread
      let html = c.items.map(i => {
        const inf = Miyu.info(i.variant_id), orig = i.original_line_price, fin = i.final_line_price, name = i.product_title || inf.n;
        const variant = i.variant_title && !/default title/i.test(i.variant_title) ? `<small>${esc(i.variant_title)}</small>` : '';
        return `<li class="dr-slot" data-key="${esc(i.key)}"><span class="dr-ring"></span>
          <a class="dr-arch" href="${esc(i.url || inf.u)}" tabindex="-1"><img src="${img(i.image || inf.img, 360)}" alt="" loading="lazy"></a>
          <a class="dr-name" href="${esc(i.url || inf.u)}">${emLast(name)}</a>
          <span class="dr-p">${fin < orig ? `${rs(fin)} <s>${rs(orig)}</s>` : rs(fin)}${variant}</span>
          <span class="dr-q" aria-label="Quantity of ${esc(name)}">
            <button data-q="${i.quantity - 1}" aria-label="${i.quantity === 1 ? 'Remove ' + esc(name) : 'One less'}">${icon(i.quantity === 1 ? 'close' : 'minus')}</button><span>${i.quantity}</span><button data-q="${i.quantity + 1}" aria-label="One more">${icon('plus')}</button>
          </span></li>`;
      }).join('');
      // empty sockets up to three: each holds a real suggestion (same family first) and says what it unlocks
      const empty = Math.max(0, O.freeQty - n);
      if (empty) {
        const inBag = new Set(c.items.map(i => i.variant_id)), fam = n ? Miyu.info(c.items[0].variant_id).f : '', seen = new Set();
        const pool = [...products.values()].filter(p => p && p.vid && p.a !== false && !inBag.has(p.vid) && !seen.has(p.vid) && seen.add(p.vid));
        pool.sort((a, b) => (b.f === fam) - (a.f === fam));
        const shop = (C.routes && C.routes.all) || '/collections/all';
        for (let j = 0; j < empty; j++) {
          const p = pool[j], u = unlock(n + j + 1);
          // an empty socket reads as empty: a dashed arch that says which piece it's waiting for, the suggestion
          // shown small (a photo bead), a + to add it — no quantity, no full-size photo like the pieces you have
          const nth = ['1st', '2nd', '3rd'][n + j] || `${n + j + 1}th`;
          html += p
            ? `<li class="dr-slot sug"><span class="dr-ring"></span>
                <button class="dr-arch dr-sg" data-vid="${p.vid}" aria-label="Add ${esc(p.n)} as your ${nth} piece"><span class="dr-slotn">Add a ${nth}</span><span class="dr-sgb" style="${esc(Miyu.info(p.vid).bf)}"><img src="${img(p.img, 200)}" alt="" loading="lazy"></span><i class="lg">+</i></button>
                <span class="dr-name">${emLast(p.n)}</span><span class="dr-p">${rs(p.p)}</span>${u ? `<span class="dr-un">+ ${u}</span>` : ''}</li>`
            : `<li class="dr-slot sug none"><span class="dr-ring"></span><a class="dr-arch" href="${esc(shop)}" aria-label="Find a piece"><span class="dr-slotn">Add a ${nth}</span><i class="lg">+</i></a>
                <span class="dr-name">Find a <em>piece</em></span>${u ? `<span class="dr-un">+ ${u}</span>` : ''}</li>`;
        }
      }
      slots.innerHTML = html;
      d.classList.toggle('many', c.items.length + empty > 3);
      // the receipt: Shopify's own numbers
      const disc = discounts(c);
      sum.innerHTML = `<dt>${n} piece${n === 1 ? '' : 's'}</dt><dd>${rs(c.original_total_price || 0)}</dd>`
        + disc.map(([t, v]) => `<dt>${esc(t)}</dt><dd class="dr-off">− ${rs(v)}</dd>`).join('')
        + `<dt>Delivery</dt><dd>${free ? '<b class="dr-free">Free</b>' : `${rs(O.fee)}<small>free with ${O.freeQty}${O.freeAmt > 0 ? ` or from ${rs(O.freeAmt)}` : ''}</small>`}</dd>`;
      // the total is what checkout will ask for: the pieces, and delivery when it isn't free yet
      const pay = (c.total_price || 0) + (n > 0 && !free ? O.fee : 0);
      $('#drTotal', d).textContent = rs(pay);
      const k = $('#drKoko', d); if (k) k.innerHTML = C.koko && pay > 0 ? `or 3 × ${rs(Math.round(pay / 3))} with KOKO` : '';
      if (isOpen) requestAnimationFrame(() => { drawThread(); if (done && wasDone === false) land(); if (!done) gsap.set(fly, { opacity: 0 }); wasDone = done; });
      else wasDone = done;
    }
    rack.addEventListener('click', e => {
      const s = e.target.closest('.dr-sg'); if (s) { cart.add(+s.dataset.vid, { from: $('img', s) }); glint($('i', s)); return; }
      const b = e.target.closest('[data-q]'); if (!b) return;
      const li = b.closest('.dr-slot'); cart.setQty(li.dataset.key, +b.dataset.q); glint(b);
    });
    addEventListener('resize', () => isOpen && drawThread());
    cart.on(c => render(c));
    render(cart.data);
    function open() {
      if (isOpen) return; isOpen = true; lastFocus = document.activeElement;
      d.hidden = false; d.classList.add('open'); lock(true); renderBag({});
      cart.refresh();
      requestAnimationFrame(() => { drawThread(); gsap.set(fly, { opacity: d.classList.contains('done') ? 1 : 0 }); });
      if (MOTION) {
        gsap.fromTo($('.dr-scrim', d), { opacity: 0 }, { opacity: 1, duration: 0.5 });
        gsap.fromTo(panel, { yPercent: innerWidth < 900 ? 100 : 0, xPercent: innerWidth < 900 ? 0 : 100 }, { yPercent: 0, xPercent: 0, duration: 0.8, ease: 'expo.out' });
        // the pieces swing on their rings as the sheet arrives
        gsap.fromTo($$('.dr-slot', d), { rotation: -7, y: -18, opacity: 0 }, { rotation: 0, y: 0, opacity: 1, duration: 1.4, ease: 'elastic.out(1, .45)', stagger: 0.07, delay: 0.2 });
        gsap.fromTo($$('.dr-msg, .dr-foot', d), { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'expo.out', stagger: 0.06, delay: 0.15 });
      }
      setTimeout(() => { const x = $('.dr-x', d); x && x.focus(); glint(x); }, 50);
    }
    function close() {
      if (!isOpen) return; isOpen = false;
      const done = () => { d.hidden = true; d.classList.remove('open'); gsap.set([panel, $('.dr-scrim', d)], { clearProps: 'all' }); };
      lock(false); renderBag({});
      if (MOTION) { gsap.to($('.dr-scrim', d), { opacity: 0, duration: 0.35 }); gsap.to(panel, { [innerWidth < 900 ? 'yPercent' : 'xPercent']: 100, duration: 0.45, ease: 'power3.in', onComplete: done }); }
      else done();
      lastFocus && lastFocus.focus && lastFocus.focus();
      document.dispatchEvent(new CustomEvent('miyu:bag-closed'));
    }
    $$('[data-close]', d).forEach(b => b.addEventListener('click', close));
    addEventListener('keydown', e => { if (e.key === 'Escape' && isOpen) close(); });
    return { open, close, get isOpen() { return isOpen; }, render };
  })();

  /* =========================================================
     QUICK VIEW — the bead grows into the page (GSAP Flip)
     ========================================================= */
  Miyu.sheet = (function () {
    const sheet = $('#sheet'); if (!sheet) return { open() {} };
    const sImg = $('#sImg'), addB = $('#sAdd'), link = $('#sLink');
    let cur = null;
    const sync = () => { if (!cur) return; const on = cart.has(cur.vid); addB.disabled = !cur.a; addB.innerHTML = !cur.a ? 'Sold out' : on ? 'In your bag <span class="bd">✓</span>' : 'Add to bag <span class="bd">+</span>'; };
    cart.on(sync);
    function open(p, fromImg) {
      if (!p) return; cur = p;
      sImg.src = fromImg ? (fromImg.currentSrc || fromImg.src) : img(p.img, 900); sImg.alt = p.n;
      $('#sFam').textContent = p.f || '';
      $('#sName').innerHTML = emLast(p.n);
      $('#sPrice').innerHTML = p.c > p.p ? `${rs(p.p)} <s>${rs(p.c)}</s>` : rs(p.p);
      $('#sKoko').textContent = C.koko ? `or 3 × ${rs(Math.round(p.p / 3))} with KOKO` : '';
      $('#sDesc').textContent = p.d || '';
      link.href = p.u;
      sync();
      sheet.classList.add('open'); sheet.style.background = p.t; lock(true);
      if (!MOTION || !fromImg) { gsap.set('#sheet [data-in]', { opacity: 1 }); return; }
      fromImg.dataset.flipId = 'piece'; sImg.dataset.flipId = 'piece';
      const state = Flip.getState(fromImg);
      gsap.fromTo(sheet, { backgroundColor: 'rgba(0,0,0,0)' }, { backgroundColor: p.t, duration: 0.5 });
      Flip.from(state, { targets: sImg, duration: 0.9, ease: 'expo.inOut', absolute: true, scale: false, props: 'borderRadius' });
      gsap.fromTo('#sheet [data-in]', { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, ease: 'expo.out', stagger: 0.07, delay: 0.45 });
    }
    function close() {
      const done = () => { sheet.classList.remove('open'); lock(false); gsap.set(sImg, { clearProps: 'all' }); gsap.set(sheet, { opacity: 1 }); };
      if (!MOTION) return done();
      gsap.to('#sheet [data-in]', { opacity: 0, y: 16, duration: 0.3, stagger: 0.03 });
      gsap.to(sheet, { opacity: 0, duration: 0.45, delay: 0.2, onComplete: done });
    }
    $('#sClose').addEventListener('click', close);
    addEventListener('keydown', e => { if (e.key === 'Escape' && sheet.classList.contains('open')) close(); });
    addB.addEventListener('click', () => { if (cur) cart.toggle(cur.vid, { from: sImg }); });
    return { open, close };
  })();

  /* =========================================================
     THE THREAD — one strand runs the whole page. Every section
     declares how it passes through (data-thread + an optional
     route hook on its instance); the route is assembled from
     whatever sections are on the page, in order. A gold needle
     leads it at your reading line; every piece in your bag rides
     along on it behind the needle.
     ========================================================= */
  Miyu.thread = (function thread() {
    const gP = $('#thrPaths'), gE = $('#thrEnds');
    const back = $('#thrLayer'), fly = $('#thrFly'), needle = $('#needle');
    if (!gP) return { rebuild() {} };
    const LINE = 0.62, TENS = 0.38, V = [0, 1], RIGHT = [1, 0], LEFT = [-1, 0];
    let X = [], Y = [], S = [], M = [], total = 0, chunks = [], cur = 0, preS = 0, drawn = -1, built = false, dirty = true;
    const beads = [];

    // hooks for sections that don't bring their own
    const GENERIC = {
      margin(T, el) { if (T.empty) T.start(el); const r = pagePos(el); T.add(T.sx(), r.y + r.h - 40, { d: V }); },
      beads(T, el) {
        const bs = $$('[data-thread-bead]', el).filter(b => b.offsetWidth);
        if (!bs.length) return GENERIC.margin(T, el);
        if (T.empty) T.start(el);
        bs.forEach(b => T.centre(b)); T.side = null;
      },
      skip() {}
    };
    function route() {
      const W = document.documentElement.clientWidth, pad = Math.max(20, Math.min(56, W * 0.05)), m = Math.round(pad * 0.5);
      const R = [];
      const T = {
        W, m, desk: W >= 900, V, RIGHT, LEFT, side: 'r', pagePos,
        add: (x, y, o) => R.push(Object.assign({ x, y }, o)),
        get empty() { return !R.length; },
        sx: s => ((s || T.side) === 'l' ? m : W - m),
        word: (sel, root) => $$(sel, root).find(e => e.offsetWidth > 0),   // SplitText can leave an empty copy of the <em>
        under(em, dir) {   // runs along the baseline of a word
          const r = pagePos(em), uy = r.y + r.h * 0.86, a = r.x - 8, b = r.x + r.w + 10;
          (dir > 0 ? [a, b] : [b, a]).forEach(x => T.add(x, uy, { d: dir > 0 ? RIGHT : LEFT }));
        },
        centre(el, o) { const r = pagePos(el); T.add(r.x + r.w / 2, r.y + r.h / 2, o); },
        lead(root, sel, dy, margin) {   // down the left margin, under the heading's last word, then off to the right margin
          const e = T.word(sel, root); if (!e) return false; const r = pagePos(e), uy = r.y + r.h * 0.86;
          T.add(m, uy - dy, { d: V }); T.add(m + 30, uy, { d: RIGHT }); T.under(e, 1);
          if (margin !== false) { T.add(W - m, uy + 50, { d: V }); T.side = 'r'; }
          return true;
        },
        start(el) { const r = pagePos(el); T.add(T.sx(), r.y + 12, { d: V, cap: true }); }
      };
      for (const el of $$('[data-thread]')) {
        if (!el.offsetHeight) continue;
        const inst = live.get(el), hook = inst && inst.api && inst.api.route, kind = el.dataset.thread;
        try { if (hook) hook(T, el); else (GENERIC[kind] || GENERIC.margin)(T, el); }
        catch (e) { console.warn('thread route', kind, e); }
      }
      if (R.length) R[R.length - 1].cap = true;
      return R;
    }

    function build() {
      const sized = [$('#thread'), back, fly];
      sized.forEach(el => { el.style.height = '0px'; });
      const R = route(), docH = document.documentElement.scrollHeight;
      sized.forEach(el => { el.style.height = docH + 'px'; });
      X = []; Y = []; S = []; M = []; chunks = []; preS = 0;
      if (R.length < 2) { gP.innerHTML = ''; gE.innerHTML = ''; built = false; return; }
      const dirAt = i => {
        if (R[i].d) return R[i].d;
        const a = R[Math.max(0, i - 1)], b = R[Math.min(R.length - 1, i + 1)], dx = b.x - a.x, dy = b.y - a.y, l = Math.hypot(dx, dy) || 1;
        return [dx / l, dy / l];
      };
      const bz = (a, b, c, d, t) => { const u = 1 - t; return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d; };
      const f = v => v.toFixed(1);
      let s = 0, my = -1e9, ch = null;
      for (let i = 0; i < R.length - 1; i++) {
        const p = R[i], q = R[i + 1], d = Math.hypot(q.x - p.x, q.y - p.y), a = dirAt(i), b = dirAt(i + 1);
        const c1 = [p.x + a[0] * d * TENS, p.y + a[1] * d * TENS], c2 = [q.x - b[0] * d * TENS, q.y - b[1] * d * TENS];
        const n = Math.max(6, Math.ceil(d / 3)), s0 = s;
        for (let k = i === 0 ? 0 : 1; k <= n; k++) {
          const t = k / n, x = bz(p.x, c1[0], c2[0], q.x, t), y = bz(p.y, c1[1], c2[1], q.y, t);
          if (X.length) s += Math.hypot(x - X[X.length - 1], y - Y[Y.length - 1]);
          my = Math.max(my, y); X.push(x); Y.push(y); S.push(s); M.push(my);
        }
        if (q.pre) preS = s;   // the opening stretch is already strung
        if (q.hidden) { ch = null; continue; }
        if (!ch || ch.len > 700) { ch = { d: `M${f(p.x)} ${f(p.y)}`, s0, len: 0 }; chunks.push(ch); }
        ch.d += `C${f(c1[0])} ${f(c1[1])} ${f(c2[0])} ${f(c2[1])} ${f(q.x)} ${f(q.y)}`;
        ch.len += s - s0;
      }
      total = s;
      gP.innerHTML = chunks.map(c => { const L = c.len.toFixed(2); return `<path d="${c.d}" pathLength="${L}" style="stroke-dasharray:${L} ${(c.len + 20).toFixed(2)};stroke-dashoffset:${L}"/>`; }).join('');
      [...gP.children].forEach((el, i) => { chunks[i].el = el; chunks[i].v = -1; });
      gE.innerHTML = R.filter(p => p.cap).map(p => `<path transform="translate(${f(p.x - 8)} ${f(p.y - 9)})" d="M1.5 9.5C3 8.8 4.4 8.6 5.6 8.9M5.6 8.9c-1.9-2.2-.3-5.4 2.4-4.8 2.6.6 2.6 4.3.1 5-1.5.4-2.6-.6-2.5-1.8M8.4 9.2c1.8.6 3.6 2 4.8 4.4" style="stroke-opacity:.75;stroke-width:1.5;stroke-linejoin:round"/>`).join('');
      gE.style.opacity = 0;
      built = true; drawn = -1; dirty = true;
    }

    const find = (A, v) => { let lo = 0, hi = A.length - 1; if (v <= A[0]) return 0; while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (A[mid] <= v) lo = mid; else hi = mid; } return A[hi] <= v ? hi : lo; };
    function pointAt(s) {
      s = Math.max(0, Math.min(total, s));
      const i = find(S, s), j = Math.min(i + 1, S.length - 1), t = S[j] > S[i] ? (s - S[i]) / (S[j] - S[i]) : 0;
      return [X[i] + (X[j] - X[i]) * t, Y[i] + (Y[j] - Y[i]) * t];
    }
    // how far along the thread the page has been read
    function targetFor(line) {
      if (line <= M[0]) return 0;
      const i = find(M, line), j = i + 1;
      if (j >= M.length) return total;
      const t = Math.min(1, (line - M[i]) / ((M[j] - M[i]) || 1));
      return S[i] + (S[j] - S[i]) * t;
    }

    function paint() {
      for (const c of chunks) {
        const v = Math.max(0, Math.min(c.len, cur - c.s0));
        if (v !== c.v) { c.v = v; c.el.style.strokeDashoffset = (c.len - v).toFixed(2); }
      }
      gE.style.opacity = cur > 1 ? 1 : 0;
      const liveN = cur > 2 && cur < total - 2;
      needle.style.opacity = liveN && MOTION ? 1 : 0;
      if (liveN) {
        const [x, y] = pointAt(cur), a = pointAt(cur + 10);
        needle.style.transform = `translate(${(x - 4).toFixed(1)}px, ${(y - 4).toFixed(1)}px) rotate(${Math.atan2(a[1] - y, a[0] - x).toFixed(3)}rad)`;
      }
      beads.forEach((b, k) => {
        const s = cur - 20 - k * 24, [x, y] = pointAt(s);
        let px = x, py = y, sc = 1;
        if (b.k < 1) { const e = b.k; px = b.fx + (x - b.fx) * e; py = b.fy + (y - b.fy) * e - Math.sin(e * Math.PI) * 60; sc = b.fs + (1 - b.fs) * e; }
        b.el.style.transform = `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) scale(${sc.toFixed(3)})`;
        b.el.style.opacity = s < 0 && b.k >= 1 ? 0 : 1;
      });
    }

    // the page's height, measured only when it changes: reading scrollHeight every frame forced a full layout
    // on every frame something else moved (the collection strand's swipe stuttered because of it)
    let maxS = 0, vh = innerHeight;
    const measureMax = () => { vh = innerHeight; maxS = document.documentElement.scrollHeight - vh; };
    measureMax();
    addEventListener('resize', measureMax);
    ScrollTrigger.addEventListener('refresh', measureMax);
    new ResizeObserver(() => requestAnimationFrame(measureMax)).observe(document.body);
    gsap.ticker.add(() => {
      if (!built) return;
      // over the last stretch of the page the reading line slides to the bottom edge, so the thread can tie off
      const y = sy, k = Math.max(0, Math.min(1, (y - maxS + vh * 0.6) / (vh * 0.6)));
      const tS = MOTION ? Math.max(preS, targetFor(y + vh * (LINE + (1.05 - LINE) * k))) : total;
      cur += (tS - cur) * 0.16; if (Math.abs(tS - cur) < 0.3) cur = tS;
      if (Math.abs(cur - drawn) < 0.2 && !dirty) return;
      drawn = cur; dirty = false; paint();
    });

    // pieces in your bag slide onto the thread
    function sync(from) {
      const vids = cart.vids;
      for (let k = beads.length - 1; k >= 0; k--) if (!vids.includes(beads[k].v)) {
        const el = beads[k].el; beads.splice(k, 1); el.classList.add('out'); setTimeout(() => el.remove(), 400);
      }
      vids.slice(0, 6).forEach(v => {
        if (beads.some(b => b.v === v)) return;
        const el = document.createElement('i'); el.className = 'tbd'; el.style.cssText = Miyu.info(v).bf; el.style.backgroundImage = `url(${img(Miyu.info(v).img, 160)})`;
        const b = { v, el, k: 1 };
        if (from && from.getBoundingClientRect && MOTION && built) {
          const r = from.getBoundingClientRect();
          Object.assign(b, { k: 0, fx: r.left + r.width / 2 + scrollX, fy: r.top + r.height / 2 + scrollY, fs: Math.min(6, r.width / 20) });
          fly.appendChild(el);
          gsap.to(b, { k: 1, duration: 1.1, ease: 'expo.inOut', onUpdate: () => { dirty = true; }, onComplete: () => { back.appendChild(el); dirty = true; } });
        } else back.appendChild(el);
        beads.push(b);
      });
      beads.sort((a, b) => vids.indexOf(a.v) - vids.indexOf(b.v));
      dirty = true;
    }
    cart.on((c, meta) => sync(meta.added ? meta.from : null));

    let bt = 0; const rebuild = () => { clearTimeout(bt); bt = setTimeout(build, 80); };
    ScrollTrigger.addEventListener('refresh', rebuild);
    document.fonts && document.fonts.ready.then(rebuild);
    new ResizeObserver(rebuild).observe(document.body);
    return { rebuild, build, sync };
  })();

  /* =========================================================
     BOOT
     ========================================================= */
  function boot() {
    readBlobs(document);
    collectThemes();
    chrome();
    $$('[data-section]').forEach(mount);
    ['#header', '#dock'].forEach(s => { const el = $(s); el && magnetic(el, makeLife(el)); });
    dockTriggers();
    cart.on(renderBag); renderBag({});
    Miyu.thread.build(); Miyu.thread.sync(null);
    ScrollTrigger.create({ start: 0, end: 'max', onUpdate: resolveTheme });
    resolveTheme();
    // desktop cursor with labels
    if (FINE && MOTION) {
      const cur = $('#cur'), curL = $('#curL');
      if (cur) {
        cur.style.display = 'block';
        const qx = gsap.quickTo(cur, 'x', { duration: 0.25, ease: 'power3' }), qy = gsap.quickTo(cur, 'y', { duration: 0.25, ease: 'power3' });
        addEventListener('pointermove', e => { qx(e.clientX); qy(e.clientY); });
        document.addEventListener('pointerover', e => { const t = e.target.closest('[data-cursor]'); if (t) { curL.textContent = t.dataset.cursor; cur.classList.add('lab'); } else cur.classList.remove('lab'); });
      }
    }
    // in-page anchors glide
    document.addEventListener('click', e => {
      const a = e.target.closest('a[href^="#"]'); if (!a) return;
      const id = a.getAttribute('href'); if (id.length < 2) return; const t = document.querySelector(id); if (!t) return;
      e.preventDefault(); scrollToY(t.getBoundingClientRect().top + scrollY);
    });
    addEventListener('load', () => { ScrollTrigger.refresh(); resolveTheme(); });
    // the cart may have changed in another tab since this page was rendered
    cart.refresh();
  }
  // deferred scripts run while readyState is 'interactive': wait for DOMContentLoaded so every section has registered
  if (document.readyState === 'complete') boot(); else document.addEventListener('DOMContentLoaded', boot);
})();
